import jsPDF from 'jspdf';
import { PurchaseOrder } from '../types/purchaseOrder';

interface TemplateColorScheme {
  primary: string;
  secondary: string;
  text: string;
  background: string;
}

interface Template {
  templateCompanyName?: string;
  templateLogo?: string | null;
  logoBase64?: string | null;
  colorScheme?: TemplateColorScheme;
  fontFamily?: string;
}

interface PurchaseOrderPdfData {
  purchaseOrder: PurchaseOrder;
  companyName?: string;
  companyAddress?: string;
  companyPhone?: string;
  companyEmail?: string;
  template?: Template;
}

const hexToRgb = (hex: string): [number, number, number] => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? [parseInt(result[1], 16), parseInt(result[2], 16), parseInt(result[3], 16)]
    : [0, 0, 0];
};

const formatCurrency = (amount: number) => `BD ${amount.toFixed(3)}`;

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });

// Classic navy layout — mirrors the Invoice "classic" PDF design: left
// logo/company block, right title + meta table, boxed vendor info, full-width
// dark table header, filled totals bar, bordered notes box.
export const generatePurchaseOrderPDF = (data: PurchaseOrderPdfData) => {
  const {
    purchaseOrder: po,
    companyName: rawCompanyName = '',
    companyAddress = '',
    companyPhone = '',
    companyEmail = '',
    template
  } = data;

  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;

  const colorScheme = template?.colorScheme;
  const navy:  [number, number, number] = colorScheme?.primary    ? hexToRgb(colorScheme.primary)    : [30, 58, 95];
  const text:  [number, number, number] = colorScheme?.text       ? hexToRgb(colorScheme.text)       : [31, 41, 55];
  const muted: [number, number, number] = colorScheme?.secondary  ? hexToRgb(colorScheme.secondary)  : [100, 116, 139];
  const boxBg: [number, number, number] = colorScheme?.background ? hexToRgb(colorScheme.background) : [248, 250, 252];

  const companyName = template?.templateCompanyName || rawCompanyName || 'Your Company';

  // ── HEADER: logo + company name (left), title + meta (right) ──────────────
  const logoSize = 24;
  const logoX = margin;
  const logoY = 10;
  let textX = margin;

  const logoSource = template?.templateLogo;
  const logoBase64 = template?.logoBase64;
  let hasLogo = false;

  if (logoBase64) {
    try { doc.addImage(logoBase64, 'PNG', logoX, logoY, logoSize, logoSize); hasLogo = true; } catch { /* skip */ }
  } else if (logoSource) {
    try {
      const url = logoSource.startsWith('http')
        ? logoSource
        : `${window.location.origin.replace(':3000', ':5002')}/${logoSource}`;
      doc.addImage(url, 'PNG', logoX, logoY, logoSize, logoSize);
      hasLogo = true;
    } catch { /* skip */ }
  }
  if (hasLogo) textX = logoX + logoSize + 6;

  const titleReserve = 85; // space reserved on the right for title + meta
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...navy);
  const nameLines = doc.splitTextToSize(companyName.toUpperCase(), pageWidth - textX - titleReserve);
  let nameY = logoY + 8;
  nameLines.forEach((line: string) => {
    doc.text(line, textX, nameY);
    nameY += 6;
  });

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(...navy);
  doc.text('PURCHASE ORDER', pageWidth - margin, 20, { align: 'right' });

  const metaRows = [
    { label: 'PO #:',      value: po.poNumber },
    { label: 'Order Date:', value: formatDate(po.orderDate) },
    ...(po.expectedDeliveryDate ? [{ label: 'Expected Delivery:', value: formatDate(po.expectedDeliveryDate) }] : []),
    { label: 'Status:',    value: po.status },
  ];
  let metaY = 28;
  doc.setFontSize(9);
  metaRows.forEach((row) => {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...muted);
    doc.text(row.label, pageWidth - margin - 55, metaY);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...text);
    doc.text(row.value, pageWidth - margin, metaY, { align: 'right' });
    metaY += 6;
  });

  let yPos = Math.max(nameY, metaY, logoY + logoSize) + 6;
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.6);
  doc.line(margin, yPos, pageWidth - margin, yPos);

  // ── VENDOR box ───────────────────────────────────────────────────────────
  yPos += 8;
  const barW = 3;
  const boxW = pageWidth - margin * 2;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  const hasVendorContact = Boolean(po.vendor?.email || po.vendor?.phone);
  const vendorContact = [po.vendor?.email, po.vendor?.phone].filter(Boolean).join(' | ');
  const boxH = 8 + 6 + (hasVendorContact ? 4.5 : 0) + 5;

  doc.setFillColor(...boxBg);
  doc.rect(margin, yPos, boxW, boxH, 'F');
  doc.setFillColor(...navy);
  doc.rect(margin, yPos, barW, boxH, 'F');

  const contentX = margin + barW + 7;
  let boxY = yPos + 7;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...muted);
  doc.text('VENDOR', contentX, boxY);
  boxY += 6;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...text);
  doc.text(po.vendor?.name || 'N/A', contentX, boxY);
  boxY += 5;

  if (hasVendorContact) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(...muted);
    doc.text(vendorContact, contentX, boxY);
  }

  yPos += boxH + 8;

  // ── ITEMS TABLE ──────────────────────────────────────────────────────────
  const tableLeft  = margin;
  const tableRight = pageWidth - margin;
  const tableW     = tableRight - tableLeft;
  const headerH    = 10;
  const colW = { product: 38, desc: 57, qty: 16, rate: 27, tax: 16, amount: 26 };

  const drawTableHeader = (y: number) => {
    doc.setFillColor(...navy);
    doc.rect(tableLeft, y, tableW, headerH, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(255, 255, 255);
    doc.text('PRODUCT', tableLeft + 3, y + 6.5);
    let hx = tableLeft + colW.product;
    doc.text('DESCRIPTION', hx + 3, y + 6.5);
    hx += colW.desc;
    doc.text('QTY', hx + 3, y + 6.5);
    hx += colW.qty;
    doc.text('RATE', hx + 3, y + 6.5);
    hx += colW.rate;
    doc.text('TAX', hx + 3, y + 6.5);
    doc.text('AMOUNT', tableRight - 3, y + 6.5, { align: 'right' });
  };

  drawTableHeader(yPos);
  yPos += headerH;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);

  (po.items || []).forEach((item) => {
    const amount = item.quantity * item.rate;
    const taxAmount = (amount * (item.taxRate || 0)) / 100;
    const total = amount + taxAmount;

    const productLines = doc.splitTextToSize(item.product?.name || 'N/A', colW.product - 5);
    const descLines = doc.splitTextToSize(item.description || '-', colW.desc - 5);
    const lineCount = Math.max(productLines.length, descLines.length);
    const rowH = Math.max(12, lineCount * 5 + 6);

    if (yPos + rowH > pageHeight - 65) {
      doc.setDrawColor(226, 232, 240);
      doc.setLineWidth(0.3);
      doc.line(tableLeft, yPos, tableRight, yPos);

      doc.addPage();
      yPos = 20;
      drawTableHeader(yPos);
      yPos += headerH;
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
    }

    const rowStartY = yPos + 5;
    doc.setTextColor(...text);
    doc.text(productLines, tableLeft + 3, rowStartY);
    doc.text(descLines, tableLeft + colW.product + 3, rowStartY);

    let cx = tableLeft + colW.product + colW.desc + 3;
    doc.text(item.quantity.toString(), cx, rowStartY);
    cx += colW.qty;
    doc.text(formatCurrency(item.rate), cx, rowStartY);
    cx += colW.rate;
    doc.text(item.taxRate ? `${item.taxRate}%` : '–', cx, rowStartY);
    doc.text(formatCurrency(total), tableRight - 3, rowStartY, { align: 'right' });

    yPos += rowH;

    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(tableLeft, yPos, tableRight, yPos);
  });

  // ── TOTALS + TOTAL bar ───────────────────────────────────────────────────
  yPos += 8;
  const summaryLeft = pageWidth - margin - 95;
  const summaryRight = pageWidth - margin;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9.5);
  doc.setTextColor(...muted);
  doc.text('Subtotal', summaryLeft, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...text);
  doc.text(formatCurrency(po.subtotal), summaryRight, yPos, { align: 'right' });
  yPos += 6.5;

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...muted);
  doc.text('Tax', summaryLeft, yPos);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...text);
  doc.text(formatCurrency(po.taxAmount), summaryRight, yPos, { align: 'right' });
  yPos += 6.5;

  if (po.shippingCost > 0) {
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...muted);
    doc.text('Shipping', summaryLeft, yPos);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...text);
    doc.text(formatCurrency(po.shippingCost), summaryRight, yPos, { align: 'right' });
    yPos += 6.5;
  }

  yPos += 1.5;
  const barH = 11;
  doc.setFillColor(...navy);
  doc.rect(summaryLeft, yPos, summaryRight - summaryLeft, barH, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10.5);
  doc.setTextColor(255, 255, 255);
  doc.text('Total', summaryLeft + 4, yPos + barH / 2 + 3.5);
  doc.text(formatCurrency(po.total), summaryRight - 4, yPos + barH / 2 + 3.5, { align: 'right' });

  // ── NOTES / TERMS ────────────────────────────────────────────────────────
  yPos += barH + 10;

  const drawTextBox = (heading: string, body: string) => {
    if (yPos > pageHeight - 50) {
      doc.addPage();
      yPos = 20;
    }
    const boxPad = 8;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    const lines = doc.splitTextToSize(body, boxW - boxPad * 2);
    const h = 10 + lines.length * 4.5 + 6;

    doc.setFillColor(...boxBg);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.rect(margin, yPos, boxW, h, 'FD');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(...text);
    doc.text(heading, margin + boxPad, yPos + 8);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(...muted);
    doc.text(lines, margin + boxPad, yPos + 14.5);

    yPos += h + 8;
  };

  if (po.notes) drawTextBox('NOTES', po.notes);
  if (po.termsAndConditions) drawTextBox('TERMS AND CONDITIONS', po.termsAndConditions);

  // ── FOOTER ───────────────────────────────────────────────────────────────
  const footerContactParts: string[] = [];
  if (companyAddress) footerContactParts.push(companyAddress);
  const footerContactLine = footerContactParts.join('  |  ');
  const footerReachLine = [companyPhone, companyEmail].filter(Boolean).join('  |  ');

  const totalPages = doc.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(margin, pageHeight - 22, pageWidth - margin, pageHeight - 22);

    let footY = pageHeight - 17;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...text);
    doc.text(companyName, pageWidth / 2, footY, { align: 'center' });
    footY += 4.5;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...muted);
    if (footerContactLine) {
      doc.text(footerContactLine, pageWidth / 2, footY, { align: 'center' });
      footY += 4;
    }
    if (footerReachLine) {
      doc.text(footerReachLine, pageWidth / 2, footY, { align: 'center' });
    }
  }

  doc.save(`PO_${po.poNumber}.pdf`);
};
